import axios from "axios";
import React, { useEffect, useState, forwardRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useHighlight } from "../../hooks/useHighlight";

import { useMatomo } from "@datapunt/matomo-tracker-react";

/** Material-UI */
import { alpha, Grid, List, ListItem, Typography, TextField, MenuItem, Box, IconButton } from "@material-ui/core";
import { ExpandMore, Check } from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/styles";

/** API */
import APIs from "../../APIs";

/** UI */
import { useDocTitle } from "../../components/_common/DocTitle";
import Preloader from "../../components/_common/Preloader";
import Paginator from "../../components/Paginator";
import Search from "../../components/Search";
import ObjectsAccordion from "./ObjectsAccordion";

const BORDERED_LAYOUT = process.env.REACT_APP_BORDERED_LAYOUT
    ? JSON.parse(process.env.REACT_APP_BORDERED_LAYOUT)
    : false;

const regex = /(<([^>]+)>)|\t|\n|&nbsp;/gi;

const DropDownIcon = forwardRef((props, ref) => (
    <IconButton ref={ref} {...props} size={"small"} component={"div"}>
        <ExpandMore color={"primary"} fontSize={"small"} />
    </IconButton>
));

const useStyles = makeStyles((theme) => ({
    bigTitle: {
        margin: theme.spacing(0, 4, 4, 4),
        textAlign: "center",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
            fontSize: "1.125rem",
            fontWeight: theme.typography.fontWeightMedium,
        },
    },
    alphabet: {
        display: "flex",
        justifyContent: "space-between",
        overflow: "hidden",
    },
    listOverlay: {
        content: '""',
        position: "absolute",
        width: "100%",
        height: 1,
        bottom: -1,
        boxShadow: `0 1px 56px 64px ${theme.palette.background.paper}`,
        zIndex: 1,
    },
    listRoot: {
        maxHeight: "calc(100vh - 0px)",
        [theme.breakpoints.down("sm")]: {
            maxHeight: "calc(100vh - 250px)",
            paddingBottom: "calc(100vh - 250px)",
        },
        overflow: "hidden",
        overflowY: "scroll",
        marginTop: theme.spacing(4),
        paddingBottom: "100vh",
        position: "relative",
        scrollBehavior: "smooth",
    },
    listSection: {
        backgroundColor: "inherit",
    },
    ul: {
        backgroundColor: "inherit",
        padding: 0,
    },
    listItemLI: {
        display: "flex",
        alignItems: "flex-start",
        flexWrap: "wrap",
        // paddingLeft: 0,
        padding: 0,

        border: BORDERED_LAYOUT ? "1px solid" : "none",
        borderColor: BORDERED_LAYOUT ? alpha(theme.palette.primary.main, 0.25) : "inherit",
        borderRadius: BORDERED_LAYOUT ? 20 : 0,
        overflow: "hidden",

        "& + &": {
            marginTop: 20,
        },
    },
    listSubheaderSticky: {
        backgroundColor: "rgb(234 237 244)",
        color: theme.palette.primary.main,
        padding: theme.spacing(6),
        textTransform: "uppercase",
        [theme.breakpoints.down("sm")]: {
            lineHeight: 1.4,
            fontWeight: theme.typography.fontWeightRegular,
        },
        width: "100%",
    },
    listItemTextPrimary: {},
    listItemSelected: {
        backgroundColor: `${alpha(theme.palette.primary.main, 0.0675)}!important`,
    },
    mainListItem: {
        width: "100%",
        fontSize: "14px",
        padding: 0,
    },
    alphabetChip: {
        backgroundColor: alpha(theme.palette.primary.light, 0.5),
        color: theme.palette.primary.contrastText,
        "&:hover": {
            backgroundColor: theme.palette.primary.main,
        },
        "&:focus": {
            backgroundColor: theme.palette.primary.main,
        },
    },
    alphabetChipAcvtive: {
        backgroundColor: theme.palette.primary.main,
    },
    alphabetAvatar: {
        backgroundColor: theme.palette.primary.main,
    },
    listItemAvatarAvatar: {
        backgroundColor: alpha(theme.palette.primary.main, 0.25),
    },
    formControl: {
        marginTop: theme.spacing(2),
        minWidth: "100%",
    },
    listItemTextTitle: {
        color: "inherit",
        textDecoration: "inherit",
        padding: 0,
        fontSize: "clamp(13px, 2vw, 1.125rem)",
    },
    ReactVirtualizedList: {
        "&::-webkit-scrollbar": {},
    },
    alert: {
        marginTop: theme.spacing(6),
        "&, & *": {
            color: "#621B16",
            fontWeight: "bolder",
        },
    },
    paginationList: {
        opacity: 1,
        "& li": {
            fontSize: "0.875rem",
            fontWeight: 400,
            lineHeight: 1.43,
        },
    },
    paginationSelect: {
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: 1.43,
        paddingBottom: 6,
    },
    paginationSelectIcon: {
        top: "calc(50% - 13px)",
    },
    searchWrapper: {
        "&, & > form": {
            display: "flex",
            alignItems: "center",
        },
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    searchForm: {
        width: "100%",
        minHeight: 80,
        display: "flex",
        gap: 16,
    },
    btnSearch: {
        margin: 0,
        height: 48,
        minWidth: 153,
        "& > span": {
            marginTop: 2,
        },
        "&:focus-visible": {
            outline: "#000 auto 2px",
            outlineOffset: 3,
        },
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(0, 0, 4, 0),
        },
    },
    textField: {
        "& label": {
            fontSize: "clamp(13px, 1vw, 1rem)",
        },
        "& fieldset": {
            [theme.breakpoints.down("sm")]: {
                top: -7,
            },
        },
        "& legend": {
            fontSize: "0.835em",
            [theme.breakpoints.down("sm")]: {
                fontSize: "0.6em",
            },
        },
        "& button": {
            top: "calc(50% - 14px)",
        },
        "& input": {
            left: -1999,
        },
    },
    input: {
        fontSize: "clamp(13px, 1vw, 1rem)",
        paddingTop: 15,
        paddingBottom: 14,
        paddingLeft: 14,
        "& .check": {
            display: "none",
        },
    },
    label: {
        fontSize: "clamp(13px, 1vw, 1rem)",
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
    },
    inputAdornmentEnd: {},
    datePickerButtonRoot: {
        "& svg": {
            color: theme.palette.primary.main,
        },
    },
    adornedEnd: {
        paddingRight: 0,
    },
    selectMenuPaper: {
        opacity: 1,
        '& li[class*="MuiListItem-selected"]': {
            backgroundColor: "transparent",
            color: theme.palette.primary.main,
            fontWeight: 500,
        },
    },
}));

const RSIOBIEKTY_Page = (props) => {
    const {
        match: { params },
    } = props;

    const classes = useStyles();
    const { t } = useTranslation();
    const { highlight } = useHighlight();
    const { trackPageView } = useMatomo();
    const history = useHistory();

    useDocTitle(t(`pages.typesPageTitle.${params?.typ_obiektu__icontains}`));

    const [apiWait, setApiWait] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 599.95);
    const [data, setData] = useState([]);
    const [SEARCH_ERROR, setSEARCH_ERROR] = useState(false);
    const [SEARCH, setSEARCH] = useState("");
    const [LOADING, setLOADING] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [count, setCount] = useState(0);
    const [QUERY, setQUERY] = useState("");
    const [SORT, setSORT] = useState("nazwa_obiektu");

    /** Process url query */
    useEffect(() => {
        const currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));

        if (!Object.keys(currentQuery)?.length) {
            setApiWait(false);
            return;
        }

        if (
            Number(currentQuery?.page) !== page ||
            Number(currentQuery?.limit) !== rowsPerPage ||
            currentQuery?.search !== encodeURI(SEARCH)
        ) {
            if (Number(currentQuery?.limit) !== rowsPerPage) {
                setRowsPerPage(Number(currentQuery?.limit));
            }

            if (currentQuery?.search !== encodeURI(SEARCH)) {
                setSEARCH(currentQuery?.search ? decodeURI(currentQuery?.search) : "");
            }

            if (Number(currentQuery?.page) !== page) {
                setPage(Number(currentQuery?.page) - 1);
            }

            setApiWait(false);
        } else {
            setApiWait(false);
        }
        // eslint-disable-next-line
    }, []);

    /** get api data */
    useEffect(() => {
        if (apiWait) return;
        setSEARCH_ERROR(false);
        getData({ page });
        return;
        // eslint-disable-next-line
    }, [SEARCH, page, rowsPerPage, apiWait]);

    /* track page view */
    useEffect(() => {
        trackPageView();
        // eslint-disable-next-line
    }, []);

    /** resize listener */
    useEffect(() => {
        window.addEventListener("resize", () => {
            setIsMobile(window.innerWidth < 599.96);
        });
    }, [isMobile]);

    /** Handlers */
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOnSubmitSearch = (newSearchText) => {
        setPage(0);
        setSEARCH(newSearchText);
    };

    const handleSort = async (event) => {
        setSORT(event.target.value);
        const ordering = event.target.value;

        const currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));
        const newQuery = {
            ...currentQuery,
            ordering,
        };

        setApiWait(true);

        await Promise.all([
            // setPage(0),
            newQuery && setQUERY(newQuery),
        ]).then((res) => {
            setApiWait(false);
            setLOADING(false);
        });
    };

    const getData = ({ page }) => {
        // let apiURL = `${APIs.dataURL.rsiobiekty}?search=${SEARCH}&limit=${rowsPerPage}&page=${page + 1}${
        //     params?.typ_obiektu__icontains
        //         ? `&typ_obiektu__icontains=${params?.typ_obiektu__icontains?.replaceAll("_", "+")}`
        //         : ""
        // }`;

        let currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));

        const newQuery = new URLSearchParams(
            QUERY || { ...currentQuery, limit: rowsPerPage, page: page + 1, search: SEARCH || "" }
        ).toString();

        let apiURL = `${APIs.dataURL.rsiobiekty}?${newQuery}${
            params?.typ_obiektu__icontains
                ? `&typ_obiektu__icontains=${params?.typ_obiektu__icontains?.replaceAll("_", "+")}`
                : ""
        }`;

        const newPath = `${history.location.pathname}?${newQuery}${history.location.hash}`;

        setTimeout(() => {
            history.push(newPath, { shallow: true });
        }, 100);

        setLOADING(true);
        axios
            .get(apiURL)
            .then((result) => {
                const apiData = result.data.results.map((result) => {
                    result.systemy.forEach((system, index) => {
                        Object.keys(system).forEach((key) => {
                            result.systemy[index][key] =
                                typeof result.systemy[index][key] === "string"
                                    ? result.systemy[index][key].replace(regex, "")
                                    : result.systemy[index][key];
                        });
                        return result;
                    });

                    result.cechyobiektu.forEach((feature, index) => {
                        Object.keys(feature).forEach((key) => {
                            result.cechyobiektu[index][key] =
                                typeof result.cechyobiektu[index][key] === "string"
                                    ? result.cechyobiektu[index][key].replace(regex, "")
                                    : result.cechyobiektu[index][key];
                        });
                        return result;
                    });
                    return {
                        ...result,
                        typ_obiektu: result.typ_obiektu || t("common.noname"),
                        nazwa_obiektu: result.nazwa_obiektu.replace(regex, ""),
                    };
                });
                // .sort(
                //     (a, b) =>
                //         a.typ_obiektu.localeCompare(b.typ_obiektu) ||
                //         a.nazwa_obiektu.toUpperCase().localeCompare(b.nazwa_obiektu.toUpperCase())
                // );

                setCount(result.data.count);
                // setFULL_DATA(apiData);
                setData(apiData);
            })
            .catch((error) => {
                if (SEARCH) {
                    setSEARCH_ERROR(
                        t('Brak wyników wyszukiwania dla frazy "{{SEARCH}}"{{dot}}<br/>{{apiError}}', {
                            SEARCH,
                            dot: ".",
                            apiError: error.response.data.detail ? `<em>${error.response.data.detail}</em>` : "",
                        })
                    );
                } else {
                    setSEARCH_ERROR(error.response?.data?.detail || t("Nieznany błąd"));
                }
            })
            .finally(() => {
                setLOADING(false);
                setQUERY("");
            });
    };

    const optionsSort = [
        { label: t("Nazwa od A do Z"), value: "nazwa_obiektu", default: true },
        { label: t("Nazwa od Z do A"), value: "-nazwa_obiektu" },
        { label: t("Data aktualizacji rosnąco"), value: "DA", date: true },
        { label: t("Data aktualizacji malejąco"), value: "DD", date: true },
    ];
    return (
        <>
            <Typography
                variant="h4"
                className={classes.bigTitle}
                color="primary"
                component={"h2"}
                id={"page-content"}
                tabIndex={0}
            >
                {t(`pages.types.${params?.typ_obiektu__icontains}`)}
            </Typography>

            <Grid container>
                <Grid item xs={12} className={classes.searchWrapper}>
                    <Search
                        label={t("common.RSIOBIEKTYsearchObject")}
                        onSubmit={(newSearchText) => handleOnSubmitSearch(newSearchText)}
                        error={SEARCH_ERROR}
                        search={SEARCH}
                    />
                </Grid>

                <Grid item xs={12} style={{ marginTop: 16 }}>
                    &nbsp;
                </Grid>

                <Grid item xs={12} style={{ position: "relative", overflow: "hidden" }} id="aGrid">
                    <div className={classes.listOverlay0} />

                    {!!SEARCH_ERROR && (
                        <Alert severity="error" className={classes.alert}>
                            <span dangerouslySetInnerHTML={{ __html: SEARCH_ERROR }} />
                        </Alert>
                    )}

                    {!!LOADING && (
                        <>
                            <Preloader type="linear" />
                            <Preloader type="linear" />
                            <Preloader type="linear" />
                        </>
                    )}

                    {!!data?.length && !LOADING && (
                        <Paginator
                            component="div"
                            count={count}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            queryParams={{ limit: rowsPerPage, page: page + 1, search: SEARCH }}
                            componentTopBefore={
                                <TextField
                                    id="obiekty-sort"
                                    select
                                    label={t("Sortowanie")}
                                    value={SORT}
                                    onChange={handleSort}
                                    variant="outlined"
                                    fullWidth={!!isMobile}
                                    SelectProps={{
                                        IconComponent: DropDownIcon,
                                        MenuProps: {
                                            getContentAnchorEl: null,
                                            anchorOrigin: { vertical: "bottom", horizontal: "center" },
                                            transformOrigin: { vertical: "top", horizontal: "center" },
                                            PaperProps: {
                                                className: classes.selectMenuPaper,
                                            },
                                        },
                                        id: "obiekty-sort-select",
                                    }}
                                    size={isMobile ? "small" : "medium"}
                                    classes={{ root: classes.textField }}
                                    inputProps={{
                                        className: classes.input,
                                    }}
                                    style={{
                                        order: isMobile ? 1 : 2,
                                        width: isMobile ? undefined : "clamp(280px, 20vw, 328px)",
                                    }}
                                    ref={(ref) => {
                                        if (ref) {
                                            const inputEl = ref.querySelector("input");

                                            if (inputEl) {
                                                setTimeout(() => {
                                                    inputEl.style.color = "#000";
                                                    inputEl.style.backgroundColor = "#FFF";
                                                    inputEl.style.opacity = 1;
                                                    inputEl.id = "obiekty-sort";
                                                }, 500);
                                            }
                                        }
                                    }}
                                >
                                    {optionsSort.map((option) => {
                                        const _hasDateField = data?.every((d) => d.hasOwnProperty("data_aktualizacji"));

                                        return option.date && !_hasDateField ? null : (
                                            <MenuItem key={option.value} value={option.value}>
                                                <Box
                                                    // component={option.default ? "em" : "span"}
                                                    className={classes.label}
                                                >
                                                    {option.label}
                                                    {option.value === SORT && (
                                                        <Check className="check" color="primary" />
                                                    )}
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            }
                        >
                            <List>
                                {data?.map((dataItem, index) => {
                                    return (
                                        <React.Fragment key={dataItem.id_obiektu}>
                                            <ListItem
                                                dense={true}
                                                className={classes.listItemLI}
                                                classes={{ selected: classes.listItemSelected }}
                                                id={`item-${dataItem.id_obiektu}`}
                                            >
                                                <List className={classes.mainListItem}>
                                                    <ListItem className={classes.listItemTextTitle}>
                                                        <div
                                                            style={{
                                                                padding: "8px 16px",
                                                                backgroundColor: "#00000007",
                                                                borderBottom: "1px solid #00000011",
                                                                flexGrow: 1,
                                                            }}
                                                        >
                                                            {/* {dataItem.nazwa_obiektu} */}
                                                            {highlight({
                                                                text: dataItem.nazwa_obiektu,
                                                                search: SEARCH,
                                                                classes,
                                                            })}
                                                        </div>
                                                    </ListItem>

                                                    {!!dataItem.systemy.length && (
                                                        <>
                                                            <ListItem
                                                                className={classes.listItemTextTitle}
                                                                style={{ marginLeft: 16, padding: "4px 16px" }}
                                                            >
                                                                <b>{t("obiekt.nazwa_systemu")}</b>:{" "}
                                                                <>{dataItem.systemy[0].nazwa_systemu}</>
                                                            </ListItem>
                                                            <ListItem
                                                                className={classes.listItemTextTitle}
                                                                style={{ marginLeft: 16, padding: "4px 16px" }}
                                                            >
                                                                <b>{t("obiekt.nazwa_systemu_skr")}</b>:{" "}
                                                                <>{dataItem.systemy[0].nazwa_systemu_skr}</>
                                                            </ListItem>
                                                            <ListItem
                                                                className={classes.listItemTextTitle}
                                                                style={{ marginLeft: 16, padding: "4px 16px" }}
                                                            >
                                                                <b>{t("obiekt.gestor")}</b>:{" "}
                                                                <>{dataItem.systemy[0].gestor}</>
                                                            </ListItem>
                                                            <ListItem className={classes.listItemTextTitle}>
                                                                <ObjectsAccordion
                                                                    id_obiektu={dataItem.id_obiektu}
                                                                    cechyobiektu={dataItem.cechyobiektu}
                                                                />
                                                            </ListItem>
                                                        </>
                                                    )}
                                                </List>
                                            </ListItem>
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        </Paginator>
                    )}
                </Grid>
            </Grid>
        </>
    );
};

export default RSIOBIEKTY_Page;

// t("pages.types.osoba_fizyczna")
// t("pages.types.podmiot")
// t("pages.types.obiekt_przestrzenny")
// t("pages.types.inny")

// t("pages.typesPageTitle.osoba_fizyczna")
// t("pages.typesPageTitle.podmiot")
// t("pages.typesPageTitle.obiekt_przestrzenny")
// t("pages.typesPageTitle.inny")
